"use client";

import { useUser } from "@/app/hooks/useUser";
import { fetchInterceptor } from "@/app/lib/fetchInterceptor";
import { UserInfoType } from "@/types/comman";
import { PlanConfig } from "@/app/types/subscription";
import { createContext, useContext, useEffect, useState } from "react";
import { useSession } from "next-auth/react";
import { ROLES } from "@/app/lib/constants";
import { getEmployeeData, setEmployeeData } from "@/app/lib/indexedDB";
import { PostHogProvider } from "posthog-js/react";
import posthog from "posthog-js";

interface UserContextType {
  organizationName: string;
  setOrganizationName: (name: string) => void;
  employeeName: string;
  setEmployeeName: (name: string) => void;
  setServiceAreaId: (id: any) => void;
  serviceAreaId: any;
  user: any;
  role: number ;
  accessiblePaths: string[];
  setAccessiblePaths: (paths: string[]) => void;
  providerType: string | null;
  fetchUserDetails: () => Promise<any>;
  serviceAreaOptions: Array<{ label: string; value: string; serviceAreaId: string, timeZone: string, organizationId?: string | null, shortCode?: string | null, organizationName?: string | null, createdAt?: string | null }>;
  setServiceAreaOptions: (options: Array<{ label: string; value: string; serviceAreaId: string, timeZone: string, organizationId?: string | null, shortCode?: string | null, organizationName?: string | null, createdAt?: string | null }>) => void;
  refreshServiceAreaOptions: () => Promise<void>;
  selectedServiceAreaTimeZone: string | null;
  setSelectedServiceAreaTimeZone: (timezone: string | null) => void;
  selectedServiceAreaStationCode: string | null;
  setSelectedServiceAreaStationCode: (code: string | null) => void;
  selectedDspOrganizationId: string | null;
  setSelectedDspOrganizationId: (id: string | null) => void;
  organizationCreatedAt: string | null;
  setOrganizationCreatedAt: (date: string | null) => void;
  planConfig: PlanConfig | null;
  planTitle: string | null; // Readable plan name from plan.title (e.g., "Business Pro", "Payroll & Compliance")
}

const userContext = createContext<UserContextType | undefined>(undefined);

export const UserContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const userInfo: UserInfoType = useUser();
  const { data: session } = useSession();
  const [organizationName, setOrganizationName] = useState("loading....");
  const [employeeName, setEmployeeName] = useState("loading...");
  const [serviceAreaId, setServiceAreaId] = useState(null);
  const [serviceAreaOptions, setServiceAreaOptions] = useState<Array<{ label: string; value: string; serviceAreaId: string, timeZone: string, organizationId?: string | null, shortCode?: string | null, organizationName?: string | null, createdAt?: string | null }>>([]);
  const [selectedServiceAreaStationCode, setSelectedServiceAreaStationCode] = useState<string | null>(null);
  const [user, setUser] = useState(null);
  const [role, setRole] = useState<number>(0);
  const [accessiblePaths, setAccessiblePaths] = useState<string[]>([]);
  const [providerType, setProviderType] = useState<string | null>(null);
  const [planConfig, setPlanConfig] = useState<PlanConfig | null>(null);
  const [planTitle, setPlanTitle] = useState<string | null>(null);
  // New state for storing the selected service area's timezone
  const [selectedServiceAreaTimeZone, setSelectedServiceAreaTimeZone] = useState<string | null>(null);
  // State for storing the selected DSP organization ID
  const [selectedDspOrganizationId, setSelectedDspOrganizationId] = useState<string | null>(null);
  // State for storing organization createdAt date (from selected service area)
  const [organizationCreatedAt, setOrganizationCreatedAt] = useState<string | null>(null);
  // State to track if PostHog has been initialized
  const [posthogInitialized, setPosthogInitialized] = useState(false);

  const interceptedFetch = fetchInterceptor(); // your existing fetch wrapper

  const fetchUserDetails = async () => {
    try {
      const response = await interceptedFetch(`${process.env.NEXT_PUBLIC_API_URL}/user`, {
        method: 'GET',
      });
      if (!response) return;

      if (response.ok) {
        const { user, role , employee, organization, subscription } = await response.json();
        const { permissionAttributes = {}, id } = role || {};
        const paths = permissionAttributes.accessiblePaths || [];
        setUser(user);
        setRole(id);
        setAccessiblePaths(paths);
        setProviderType(organization?.providerType || null);
        setPlanConfig(subscription?.planConfig || null);
        setPlanTitle(subscription?.planTitle || null);
        // set employee id in indexed db
        await setEmployeeData({id:employee.id});
        
        return { defaultPage: role.defaultPage }
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
  };

  // Function to refresh service area options
  const refreshServiceAreaOptions = async () => {
    try {
      const {roleId}:any = session?.user || null;

      if(roleId === ROLES.DRIVER) {
        setSelectedServiceAreaTimeZone(null);
        return;
      }

      const response = await interceptedFetch(`${process.env.NEXT_PUBLIC_API_URL}/organization/layout`, {
        method: 'GET',
      });
      
      if (!response) return;
      
      const organizations = await response.json();
      
      if (!organizations || organizations.length === 0) {
        return; // No organizations available
      }
      
      // Flatten all service areas from all organizations into a single array
      let allServiceAreas: Array<any> = [];
      
      organizations.forEach((org: any) => {
        if (org.serviceareas && org.serviceareas.length > 0) {
          // Add organization info to each service area
          const orgServiceAreas = org.serviceareas.map((area: any) => ({
            ...area,
            organizationId: org.orgid,
            shortCode: org.orgshortcode,
            organizationName: org.orgname
          }));
          
          allServiceAreas = [...allServiceAreas, ...orgServiceAreas];
        }
      });
      
      if (allServiceAreas.length === 0) {
        return; // No service areas available
      }
      
      // Get the first service area's timezone if available
      const firstServiceAreaTimeZone = allServiceAreas.length > 0 ? allServiceAreas[0].timeZone : null;
      
      // Get the first service area's createdAt if available
      const firstServiceAreaCreatedAt = allServiceAreas.length > 0 ? allServiceAreas[0].createdAt : null;
      
      // Add 'All' option at the beginning with the timezone and createdAt from the first service area
      const options = [
        {
          label: 'All',
          value: 'All',
          serviceAreaId: 'All',
          timeZone: firstServiceAreaTimeZone, // Use the first service area's timezone
          organizationId: null,
          shortCode: null,
          createdAt: firstServiceAreaCreatedAt // Use the first service area's createdAt
        },
        ...allServiceAreas
      ];
      
      setServiceAreaOptions(options);
      
      // If serviceAreaId is already 'All', update the timezone and createdAt
      if (serviceAreaId === 'All' && firstServiceAreaTimeZone) {
        setSelectedServiceAreaTimeZone(firstServiceAreaTimeZone);
      }
      if (serviceAreaId === 'All' && firstServiceAreaCreatedAt) {
        setOrganizationCreatedAt(firstServiceAreaCreatedAt);
      }
    } catch (error) {
      console.error("Error fetching service areas:", error);
    }
  };

  // Call fetchUserDetails only when user is loggedIn
  useEffect(() => {
    // Only fetch user details if user is authenticated (logged in)

    if(!session) return
    
    fetchUserDetails();
  }, []);
  
  useEffect(() => {
    if (userInfo && userInfo.organizationName) {
      setOrganizationName(userInfo.organizationName);
    }
  }, [userInfo]);

  // Initialize PostHog on the client side (only once, only in production)
  useEffect(() => {
    const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
    const posthogHost = process.env.NEXT_PUBLIC_POSTHOG_HOST;
    const stage = process.env.NEXT_PUBLIC_STAGE;
    
    // Only initialize if: production env + user info available + not already initialized
    if (stage === 'production' && userInfo && userInfo.roleId && !posthogInitialized && posthogKey && posthogHost) {
      let disableSessionRecording = false;
      
      // Disable session recording for driver role
      if (userInfo?.roleId === ROLES.DRIVER) {
        disableSessionRecording = true;
      }
      
      // Initialize PostHog with role-based configuration
      posthog.init(posthogKey, {
        api_host: posthogHost,
        defaults: '2025-05-24',
        disable_session_recording: disableSessionRecording,
      });
      
      // Mark as initialized to prevent re-initialization
      setPosthogInitialized(true);
    }
  }, [userInfo, posthogInitialized]);
  
  // Custom setter for serviceAreaId that also handles timezone logic
  const setServiceAreaIdWithTimezone = (id: any) => {
    setServiceAreaId(id);
    // If 'All' is selected, use the timezone from the first non-'All' service area
    if (id === 'All' && serviceAreaOptions.length > 1) {
      // Find the first non-'All' service area with a timezone
      const firstServiceArea = serviceAreaOptions.find(option => 
        option.serviceAreaId !== 'All' && option.timeZone
      );
      
      // Set the timezone if found
      if (firstServiceArea && firstServiceArea.timeZone) {
        setSelectedServiceAreaTimeZone(firstServiceArea.timeZone);
      }
      
      // If 'All' is selected, use createdAt from the first non-'All' service area
      if (firstServiceArea && firstServiceArea.createdAt) {
        setOrganizationCreatedAt(firstServiceArea.createdAt);
      }
    } else {
      // If a specific service area is selected, use its createdAt
      const selectedOption = serviceAreaOptions.find(option => option.serviceAreaId === id);
      if (selectedOption && selectedOption.createdAt) {
        setOrganizationCreatedAt(selectedOption.createdAt);
      }
    }
  };

  return (
    <userContext.Provider
      value={{
        organizationName,
        setOrganizationName,
        employeeName,
        setEmployeeName,
        setServiceAreaId: setServiceAreaIdWithTimezone,
        serviceAreaId,
        user, role, accessiblePaths,
        setAccessiblePaths,
        providerType,
        fetchUserDetails,
        serviceAreaOptions,
        setServiceAreaOptions,
        refreshServiceAreaOptions,
        selectedServiceAreaTimeZone,
        setSelectedServiceAreaTimeZone,
        selectedServiceAreaStationCode,
        setSelectedServiceAreaStationCode,
        selectedDspOrganizationId,
        setSelectedDspOrganizationId,
        organizationCreatedAt,
        setOrganizationCreatedAt,
        planConfig,
        planTitle
      }}
    >
      <PostHogProvider client={posthog}>
        {children}
      </PostHogProvider>
    </userContext.Provider>
  );
};

export const useUserContext = () => {
  const context = useContext(userContext);
  if (!context) {
    throw new Error("useUserContext must be used within a UserProvider");
  }
  return context;
};